import {
    TRecipientPlasticInfo,
    TRecipientPlasticState,
} from '@/src/modules/build-a-card/inputs/types'
import {
    ADD_CARD,
    REMOVE_CARD,
    REMOVE_RECIPIENT,
    SET_GIFT_STATE,
    SET_RECIPIENT,
    VALIDATE_RECIPIENTS,
} from '@/src/modules/build-a-card/constants'
import { decrement, increment } from '@/src/common/utilities/setQuantity'
import { step2schema } from '@/src/modules/build-a-card/validations/schemas'

type Payload = {
    recipientIndex: number
}

type SetRecipientPayload = Payload & {
    senderName: string
    recipientMessage: string
    recipientName: string | null
    // address1: string
    // address2?: string
    // city: string
    // state: string
    // zip: string
}

type SetGiftStatePayload = {
    isEditing: boolean
    listOfRecipients: TRecipientPlasticInfo[]
    cardQuantity: number
    isHandwritten: boolean
    hasGreetingCard: boolean
}

type SetRecipient = {
    type: typeof SET_RECIPIENT
    payload?: SetRecipientPayload
}

type AddCard = {
    type: typeof ADD_CARD
    payload: Payload & { defaultRecipient?: TRecipientPlasticInfo }
}

type RemoveCard = {
    type: typeof REMOVE_CARD
    payload: Payload
}

type RemoveRecipient = {
    type: typeof REMOVE_RECIPIENT
    payload: Payload
}

type SetGiftState = {
    type: typeof SET_GIFT_STATE
    payload: SetGiftStatePayload
}

type ValidateRecipients = {
    type: typeof VALIDATE_RECIPIENTS
    payload: {}
}

type Actions =
    | SetRecipient
    | AddCard
    | RemoveCard
    | RemoveRecipient
    | SetGiftState
    | ValidateRecipients

export function recipientPlasticReducer(
    state: TRecipientPlasticState,
    action: Actions
) {
    const { type, payload } = action
    const {
        minValue,
        maxValue,
        cardQuantity,
        setCardQuantity,
        setListOfRecipients,
    } = state

    switch (type) {
        case ADD_CARD:
            increment({
                minValue: minValue,
                maxValue: maxValue,
                quantity: cardQuantity,
                setQuantity: setCardQuantity,
                setListOfRecipients: setListOfRecipients,
                isPlastic: true,
                defaultRecipient: payload?.defaultRecipient,
            }).then(() => {})
            return state
        case REMOVE_CARD:
            decrement({
                minValue: minValue,
                maxValue: maxValue,
                quantity: cardQuantity,
                setQuantity: setCardQuantity,
                setListOfRecipients: setListOfRecipients,
            }).then(() => {})
            return state
        case SET_RECIPIENT:
            const index: number = payload?.recipientIndex ?? 0
            state?.setListOfRecipients((previous: TRecipientPlasticInfo[]) => {
                const newList = [...previous]
                step2schema(state.hasGreetingCard)
                    .validate(
                        {
                            recipientName: payload?.recipientName,
                            recipientMessage: payload?.recipientMessage,
                            senderName: payload?.senderName,
                        },
                        { abortEarly: false }
                    )
                    .then(() => {
                        newList[index].recipientHasError = false
                    })
                    .catch(() => {
                        newList[index].recipientHasError = true
                    })

                newList[index] = {
                    ...newList[index],
                    senderName: payload?.senderName ?? '',
                    recipientMessage: payload?.recipientMessage ?? '',
                    recipientName: payload?.recipientName ?? '',
                }
                return [...newList]
            })

            return state
        case REMOVE_RECIPIENT:
            setListOfRecipients((previous: TRecipientPlasticInfo[]) => {
                const newList = [...previous]
                newList.splice(payload?.recipientIndex, 1)
                return [...newList]
            })
            setCardQuantity(cardQuantity - 1)
            return state
        case SET_GIFT_STATE:
            return {
                ...state,
                isEditing: payload?.isEditing ?? state.isEditing,
                listOfRecipients:
                    payload?.listOfRecipients ?? state.listOfRecipients,
                cardQuantity: payload?.cardQuantity ?? state.cardQuantity,
                isHandwritten: payload?.isHandwritten ?? state.isHandwritten,
                hasGreetingCard:
                    payload?.hasGreetingCard ?? state.hasGreetingCard,
            }
        case VALIDATE_RECIPIENTS:
            state.listOfRecipients.forEach((recipient, index) => {
                step2schema(state.hasGreetingCard)
                    .validate(
                        {
                            recipientName: recipient?.recipientName,
                            recipientMessage: recipient?.recipientMessage,
                            senderName: recipient?.senderName,
                        },
                        { abortEarly: false }
                    )
                    .then(() => {
                        state.listOfRecipients[index].recipientHasError = false
                    })
                    .catch(() => {
                        state.listOfRecipients[index].recipientHasError = true
                    })
            })
            return { ...state }
        default:
            return state
    }
}
